import { CommonModule, DatePipe } from '@angular/common';
import { Component, DestroyRef, EventEmitter, Input, Output, inject } from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { SelectionModel } from '@angular/cdk/collections';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Specimen } from 'fhir/r4';

import { SpecimenSelectionComponent } from '../specimen-selection/specimen-selection.component';
import { NaPipe } from '../../shared/na.pipe';

@Component({
    selector: 'app-selected-specimens',
    standalone: true,
    imports: [
        CommonModule,
        MatDialogModule,
        MatTableModule,
        NaPipe,
        DatePipe,
        MatCheckboxModule,
        MatButtonModule,
        MatIconModule
    ],
    templateUrl: './selected-specimens.component.html',
    styleUrl: './selected-specimens.component.scss'
})
export class SelectedSpecimensComponent {
    @Input() selectedSpecimens: Specimen[] = [];
    @Input() editable = false;
    @Input() serviceRequestIds?: string[];

    @Output() selectionChanged = new EventEmitter<Specimen[]>();

    private readonly destroyRef = inject(DestroyRef);
    private readonly dialog = inject(MatDialog);

    dataSource = new MatTableDataSource<Specimen>([]);
    selection = new SelectionModel<Specimen>(true, []);

    tableColumns: string[] = ['select', 'type', 'status', 'receivedTime'];

    ngOnInit(): void {
        this.setRows(this.selectedSpecimens);

        this.selection.changed
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.selectionChanged.emit([...this.selection.selected]);
            });
    }

    ngOnChanges(): void {
        this.setRows(this.selectedSpecimens);
    }

    private setRows(rows: Specimen[]): void {
        const next = rows ?? [];
        this.dataSource.data = next;

        // Default behavior: treat all rows as included.
        this.selection.clear();
        if (next.length) this.selection.select(...next);
    }

    // --- Selection helpers
    isAllSelected(): boolean {
        const numSelected = this.selection.selected.length;
        const numRows = this.dataSource.data.length;
        return numRows > 0 && numSelected === numRows;
    }

    toggleAllRows(): void {
        if (this.isAllSelected()) {
            this.selection.clear();
            return;
        }
        this.selection.select(...this.dataSource.data);
    }

    checkboxLabel(row?: Specimen): string {
        if (!row) {
            return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
        }
        return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row`;
    }

    // --- Delete helpers
    deleteRow(row: Specimen): void {
        if (!this.editable) return;

        const next = (this.dataSource.data ?? []).filter(r => r !== row);
        this.selectedSpecimens = next;
        this.dataSource.data = next;
        this.selection.deselect(row);
        this.selectionChanged.emit([...this.selection.selected]);
    }

    deleteSelected(): void {
        if (!this.editable) return;

        const selected = new Set(this.selection.selected);
        if (selected.size === 0) return;

        const next = (this.dataSource.data ?? []).filter(r => !selected.has(r));
        this.selectedSpecimens = next;
        this.dataSource.data = next;

        this.selection.clear();
        if (next.length) this.selection.select(...next);

        this.selectionChanged.emit([...this.selection.selected]);
    }

    addMore(): void {
        const ids = this.serviceRequestIds ?? [];

        const ref = this.dialog.open(SpecimenSelectionComponent, {
            width: '900px',
            maxHeight: '90vh',
            data: {
                serviceRequestIds: ids,
                specimensToMatch: this.dataSource.data ?? []
            }
        });

        ref.afterClosed().subscribe((result: any) => {
            const picked = (result?.selectedSpecimens ?? []) as Specimen[];
            if (!picked.length) return;

            const merged = new Map<string, Specimen>();
            const keyOf = (s: any) => {
                const id = (s?.id ?? '').toString();
                return id ? `Specimen/${id}` : JSON.stringify(s);
            };

            for (const s of (this.dataSource.data ?? [])) merged.set(keyOf(s), s);
            for (const s of picked) merged.set(keyOf(s), s);

            const next = Array.from(merged.values());
            this.selectedSpecimens = next;
            this.dataSource.data = next;

            this.selection.clear();
            if (next.length) this.selection.select(...next);

            this.selectionChanged.emit([...this.selection.selected]);
        });
    }

    getType(s: any): string {
        return s?.type?.text ?? s?.type?.coding?.[0]?.display ?? s?.type?.coding?.[0]?.code ?? '—';
    }

    getStatus(s: any): string {
        return s?.status ?? '—';
    }

    getReceivedTime(s: any): string {
        return s?.receivedTime ?? s?.collection?.collectedDateTime ?? '—';
    }
}
